// Records anonymous clicks (see shared/clicks.ts), plus the same counters per
// approximate country, taken from the device time zone (no location lookup, no
// IP; "ZZ" when the zone names no country). Fire-and-forget: a failed
// write is logged and never shown, and nothing waits on it. Each story counts
// at most once per kind per browser tab session, so reloads and repeat clicks
// don't inflate the numbers.
import { doc, increment, serverTimestamp, writeBatch, type Firestore } from "firebase/firestore/lite";
import { clickDayId, countryDayId, utcDay, type ClickKind } from "../../shared/clicks.ts";
import { UNKNOWN_COUNTRY } from "../../shared/trends.ts";

const SESSION_KEY = "nsp.clicked.v1";
const ID = /^[a-z0-9][a-z0-9_-]{0,127}$/;

const pending = new Set<string>();

function seenThisSession(): Set<string> {
  try {
    return new Set<string>(JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? "[]"));
  } catch {
    return new Set();
  }
}

/** Marked only after a successful write, so a failed one is retried on the next click. */
function markSeen(key: string): void {
  try {
    const seen = seenThisSession();
    seen.add(key);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...seen].slice(-500)));
  } catch {
    /* storage blocked: may count again next time */
  }
}

export function recordClick(db: Firestore, storyId: string, kind: ClickKind): void {
  const key = `${kind}:${storyId}`;
  if (!ID.test(storyId) || pending.has(key) || seenThisSession().has(key)) return;
  pending.add(key);
  void write(db, storyId, kind).then(
    () => markSeen(key),
    (err) => console.warn("Click not recorded", err),
  ).finally(() => pending.delete(key));
}

async function readerCountry(): Promise<string> {
  try {
    const { countryForTimeZone } = await import("../../shared/tzCountry.ts");
    return countryForTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone) ?? UNKNOWN_COUNTRY;
  } catch {
    return UNKNOWN_COUNTRY;
  }
}

async function write(db: Firestore, storyId: string, kind: ClickKind): Promise<void> {
  const country = await readerCountry();
  const day = utcDay(Date.now());
  // +1 on one counter; increment(0) on the others creates them at 0 (and leaves existing values alone).
  const bump = { opens: increment(kind === "opens" ? 1 : 0), sources: increment(kind === "sources" ? 1 : 0), saves: increment(kind === "saves" ? 1 : 0), updatedAt: serverTimestamp() };
  const batch = writeBatch(db);
  batch.set(doc(db, "clicks", storyId), { storyId, ...bump }, { merge: true });
  batch.set(doc(db, "clickDays", clickDayId(day, storyId)), { storyId, day, ...bump }, { merge: true });
  batch.set(doc(db, "countryDays", countryDayId(day, country)), { country, day, ...bump }, { merge: true });
  await batch.commit();
}

/** Attribute for links out to a story's official source; a document-level listener counts them. */
export const SOURCE_LINK_ATTR = "data-source-of";
